/**
 * Formats d'affichage du back-office (cahier des charges §4) :
 * dates en Europe/Paris, montants en euros, crédits en entiers.
 */

const FUSEAU = 'Europe/Paris';

const dateHeure = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const dateSeule = new Intl.DateTimeFormat('fr-FR', { timeZone: FUSEAU, day: '2-digit', month: '2-digit', year: 'numeric' });

const euros = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
const entier = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

/** « 29/09/2026 14:05 » ; tiret si absent. */
export function formatDateTime(valeur: string | Date | null | undefined): string {
  if (!valeur) return '—';
  const d = typeof valeur === 'string' ? new Date(valeur) : valeur;
  return Number.isNaN(d.getTime()) ? '—' : dateHeure.format(d).replace(',', '');
}

/** « 29/09/2026 » ; tiret si absent. */
export function formatDate(valeur: string | Date | null | undefined): string {
  if (!valeur) return '—';
  const d = typeof valeur === 'string' ? new Date(valeur) : valeur;
  return Number.isNaN(d.getTime()) ? '—' : dateSeule.format(d);
}

/** Centimes → « 9,90 € ». */
export function formatEuros(centimes: number): string {
  return euros.format(centimes / 100);
}

/** « 1 250 » (espace fine insécable des milliers). */
export function formatNombre(n: number): string {
  return entier.format(n);
}

/** « 1 crédit », « 50 crédits ». */
export function formatCredits(n: number): string {
  return `${formatNombre(n)} ${Math.abs(n) > 1 ? 'crédits' : 'crédit'}`;
}


/** « +50 », « −10 » (vrai signe moins), « 0 ». */
export function formatDelta(n: number): string {
  if (n > 0) return `+${formatNombre(n)}`;
  if (n < 0) return `−${formatNombre(-n)}`;
  return '0';
}

/** Variation entre deux périodes : « +12 % », « −3 % », « = » ; null sans base de comparaison. */
export function formatVariation(actuel: number, precedent: number): { texte: string; sens: 'hausse' | 'baisse' | 'stable' } | null {
  if (precedent === 0) return actuel === 0 ? { texte: '=', sens: 'stable' } : null;
  const pct = Math.round(((actuel - precedent) / precedent) * 100);
  if (pct === 0) return { texte: '=', sens: 'stable' };
  return { texte: `${pct > 0 ? '+' : '−'}${formatNombre(Math.abs(pct))} %`, sens: pct > 0 ? 'hausse' : 'baisse' };
}

/** Date du jour à Paris pour un champ <input type="date"> (AAAA-MM-JJ). */
export function jourParis(d: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: FUSEAU }).format(d);
}

const heureParis = new Intl.DateTimeFormat('sv-SE', {
  timeZone: FUSEAU,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/**
 * Début (00:00:00) ou fin (23:59:59.999) d'un jour saisi (AAAA-MM-JJ) à Paris,
 * en ISO UTC. On essaie les deux décalages possibles (été, hiver) et garde
 * celui qui redonne exactement cette heure-là à Paris.
 */
export function bornesJourParis(jour: string, borne: 'debut' | 'fin'): string {
  const heure = borne === 'debut' ? '00:00:00' : '23:59:59';
  for (const decalage of ['+02:00', '+01:00']) {
    const d = new Date(`${jour}T${heure}${borne === 'fin' ? '.999' : ''}${decalage}`);
    if (!Number.isNaN(d.getTime()) && heureParis.format(d) === `${jour} ${heure}`) return d.toISOString();
  }
  return new Date(`${jour}T${heure}Z`).toISOString();
}

/** Instant ISO → valeur d'un <input type="datetime-local"> à l'heure de Paris (« 2026-06-01T22:00 »). */
export function saisieParis(valeur: string | Date | null | undefined): string {
  if (!valeur) return '';
  const d = typeof valeur === 'string' ? new Date(valeur) : valeur;
  if (Number.isNaN(d.getTime())) return '';
  return heureParis.format(d).slice(0, 16).replace(' ', 'T');
}

/**
 * Saisie date-heure à l'heure de Paris (« 2026-06-01T22:00 ») → ISO UTC. Même
 * principe que bornesJourParis : on garde le décalage (été ou hiver) qui
 * redonne cette heure-là à Paris. Une heure qui n'existe pas (passage à
 * l'heure d'été) est prise en heure d'hiver. Null si la saisie est invalide.
 */
export function depuisSaisieParis(saisie: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(saisie)) return null;
  const attendu = `${saisie.replace('T', ' ')}:00`;
  for (const decalage of ['+02:00', '+01:00']) {
    const d = new Date(`${saisie}:00${decalage}`);
    if (!Number.isNaN(d.getTime()) && heureParis.format(d) === attendu) return d.toISOString();
  }
  const d = new Date(`${saisie}:00+01:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Centimes → saisie en euros (« 9,90 »). */
export function saisieEuros(centimes: number): string {
  return (centimes / 100).toFixed(2).replace('.', ',');
}

/** Saisie en euros (« 9,90 », « 9.9 », « 10 ») → centimes ; null si ce n'est pas un montant. */
export function centimesDepuisSaisie(saisie: string): number | null {
  const texte = saisie.trim().replace(/\s|€/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(texte)) return null;
  return Math.round(Number(texte) * 100);
}

/** Prix d'un crédit : « 0,099 € » (trois décimales sous 1 €). */
export function formatPrixCredit(centimes: number, credits: number): string {
  if (credits <= 0) return '—';
  const euros = centimes / 100 / credits;
  return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: euros < 1 ? 3 : 2 }).format(euros)} €`;
}
